import {useMemo, useState} from 'react';
import {useApolloClient, useMutation, useQuery} from '@apollo/client';
import {useNavigate, useParams} from 'react-router-dom';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Switch,
} from 'antd';
import {ArrowLeftOutlined} from '@ant-design/icons';
import {toast} from 'react-toastify';

import {UPDATE_WALLET_CURRENCY_SETTINGS} from 'graphql/mutations/wallet';
import {GET_PLATFORM_WALLET_CURRENCY_BY_NAME} from 'graphql/queries/wallet';
import {GET_ALL_COUNTRIES} from 'graphql/queries/users';
import {enabledPaymentMethod, walletCurrencies} from 'utils/mock';
import {currencyOptions} from 'utils/currencyFormatting';

import CurrencyConfirmModal from './CurrencyConfirmModal';
import PaymentMethodConfigEditor from './PaymentMethodConfigEditor';
import {
  buildPaymentMethodConfigs,
  configsToFormState,
  findConfigError,
} from './paymentMethodConfig';
import {toNumber, wholeAmountInputProps, wholeNumberRule} from './amountField';
import styles from './createWalletCurrency.module.scss';

const methodOptions = enabledPaymentMethod.map(item => ({
  value: item.key,
  label: item.value,
}));

// Daily/weekly/monthly validate against each other, so whenever one changes the
// other two have to be re-checked. antd's `dependencies` alone proved unreliable
// here: fixing the weekly figure left a stale "cannot be above the weekly limit"
// error sitting on the daily field until it was edited again.
const LIMIT_GROUPS = [
  ['depositDailyLimit', 'depositWeeklyLimit', 'depositMonthlyLimit'],
  ['withdrawalDailyLimit', 'withdrawalWeeklyLimit', 'withdrawalMonthlyLimit'],
];

const DEFAULTS = {
  currencyEnabled: true,
  displayEnabled: true,
  depositEnabled: true,
  withdrawalEnabled: true,
  depositMethods: [],
  withdrawalMethods: [],
  depositMethodConfigs: {},
  withdrawalMethodConfigs: {},
};

/** Flattens the API shape into the flat keys the form and confirm modal use. */
const toFormValues = record => ({
  currency: record?.currency,
  currencyName: record?.currencyName,
  currencySymbol: record?.currencySymbol,
  currencyEnabled: !!record?.enabled,
  displayEnabled: !!record?.displayCurrency?.enabled,
  depositEnabled: !!record?.deposit?.enabled,
  depositMethods: record?.deposit?.enabledPaymentMethods ?? [],
  depositDailyLimit: record?.deposit?.limit?.daily,
  depositWeeklyLimit: record?.deposit?.limit?.weekly,
  depositMonthlyLimit: record?.deposit?.limit?.monthly,
  // Keyed by method so the editor can address a row directly, and so a
  // duplicate `method` collapses the way the read path collapses it.
  depositMethodConfigs: configsToFormState(
    record?.deposit?.paymentMethodConfigs,
  ),
  withdrawalEnabled: !!record?.withdrawal?.enabled,
  withdrawalMethods: record?.withdrawal?.enabledPaymentMethods ?? [],
  withdrawalDailyLimit: record?.withdrawal?.limit?.daily,
  withdrawalWeeklyLimit: record?.withdrawal?.limit?.weekly,
  withdrawalMonthlyLimit: record?.withdrawal?.limit?.monthly,
  withdrawalMethodConfigs: configsToFormState(
    record?.withdrawal?.paymentMethodConfigs,
  ),
});

const CreateWalletCurrency = () => {
  const {currency} = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const client = useApolloClient();
  const isUpdate = !!currency;

  const [confirming, setConfirming] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  const {data, loading, error} = useQuery(
    GET_PLATFORM_WALLET_CURRENCY_BY_NAME,
    {
      variables: {currency},
      skip: !isUpdate,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      // The form is populated straight from the response rather than from a
      // pile of useState mirrors. The old screen kept both and only synced
      // some of them, so an untouched form could submit values that were never
      // on screen.
      onCompleted: response => {
        const record = response?.getSupportedCurrencyByCurrencyName;
        if (record?.__typename === 'Error') return;
        form.setFieldsValue(toFormValues(record));
      },
    },
  );

  const [updateWalletCurrencySettings, {loading: saving}] = useMutation(
    UPDATE_WALLET_CURRENCY_SETTINGS,
  );

  // `countryCode` here is ISO-2 uppercase, which is what the service compares
  // against a user's `countryISO`. A three-letter code would save cleanly and
  // then match nobody.
  const {data: countriesData} = useQuery(GET_ALL_COUNTRIES, {
    errorPolicy: 'all',
  });
  const countries = countriesData?.getAllCountries ?? [];

  const record = data?.getSupportedCurrencyByCurrencyName;
  const loadError =
    error?.message ??
    (record?.__typename === 'Error' ? record.message : undefined);

  const original = useMemo(
    () => (isUpdate && record?.currency ? toFormValues(record) : null),
    [isUpdate, record],
  );

  /**
   * Limits have to climb: daily <= weekly <= monthly. Equal values are allowed
   * — a currency capped at one figure across all three is a legitimate setup
   * that the old strict comparison refused to save.
   */
  const limitRule = (
    field,
    lowerField,
    upperField,
    lowerLabel,
    upperLabel,
  ) => ({
    validator: async (_, value) => {
      const amount = toNumber(value);
      if (amount === undefined) return;
      const lower = toNumber(form.getFieldValue(lowerField));
      const upper = toNumber(form.getFieldValue(upperField));
      if (lower !== undefined && amount < lower) {
        throw new Error(`Cannot be below the ${lowerLabel} limit`);
      }
      if (upper !== undefined && amount > upper) {
        throw new Error(`Cannot be above the ${upperLabel} limit`);
      }
    },
  });

  const limitFields = kind => {
    const daily = `${kind}DailyLimit`;
    const weekly = `${kind}WeeklyLimit`;
    const monthly = `${kind}MonthlyLimit`;
    return [
      {
        name: daily,
        label: 'Daily limit',
        deps: [weekly, monthly],
        rule: limitRule(daily, null, weekly, '', 'weekly'),
      },
      {
        name: weekly,
        label: 'Weekly limit',
        deps: [daily, monthly],
        rule: limitRule(weekly, daily, monthly, 'daily', 'monthly'),
      },
      {
        name: monthly,
        label: 'Monthly limit',
        deps: [daily, weekly],
        rule: limitRule(monthly, weekly, null, 'weekly', ''),
      },
    ];
  };

  /**
   * Re-validates the siblings of whichever limit just changed, so correcting
   * one figure clears the error it was causing on another.
   *
   * Two details matter here. The re-check is deferred a tick because the form
   * store has not committed the new value yet while onValuesChange runs, and
   * it skips siblings that are still empty — validating those would stamp
   * "Required" on fields the operator has not reached.
   */
  const handleValuesChange = changed => {
    const changedField = Object.keys(changed ?? {})[0];
    const group = LIMIT_GROUPS.find(fields => fields.includes(changedField));
    if (!group) return;

    setTimeout(() => {
      const siblings = group.filter(field => {
        if (field === changedField) return false;
        const value = form.getFieldValue(field);
        return value !== undefined && value !== null && value !== '';
      });
      if (!siblings.length) return;
      // Failures are already rendered under each field.
      form.validateFields(siblings).catch(() => {});
    }, 0);
  };

  const handleFinish = values => {
    // Checked before the modal opens rather than after confirming: the service
    // reports a bad logo URL or country code as one generic failure against
    // the whole currency, with nothing naming the method at fault.
    const configError =
      findConfigError(
        values.depositMethods,
        values.depositMethodConfigs,
        'Deposit',
      ) ||
      findConfigError(
        values.withdrawalMethods,
        values.withdrawalMethodConfigs,
        'Withdrawal',
      );
    if (configError) return toast.error(configError);

    setPendingValues(values);
    setConfirming(true);
  };

  /**
   * The mutation replaces the whole record, so a save always writes all
   * fifteen fields — including the ones loaded when the page opened. If
   * someone else changed this currency in the meantime, saving would quietly
   * revert their edit while the confirm modal promised "these are the only
   * values that will change". Re-read before writing and refuse if the record
   * moved underneath us.
   */
  const hasRecordChangedSinceLoad = async () => {
    if (!isUpdate || !original) return false;
    try {
      const {data: fresh} = await client.query({
        query: GET_PLATFORM_WALLET_CURRENCY_BY_NAME,
        variables: {currency},
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      });
      const latest = fresh?.getSupportedCurrencyByCurrencyName;
      if (!latest || latest.__typename === 'Error') return false;
      return JSON.stringify(toFormValues(latest)) !== JSON.stringify(original);
    } catch (checkError) {
      // If the check itself fails, let the save proceed rather than block it.
      return false;
    }
  };

  const handleConfirm = async () => {
    const values = pendingValues;

    if (await hasRecordChangedSinceLoad()) {
      toast.error(
        `${currency} was changed by someone else since you opened this page. Reload to see the current settings before saving.`,
      );
      return;
    }

    updateWalletCurrencySettings({
      variables: {
        input: {
          currency: values.currency,
          currencyName: values.currencyName?.trim(),
          currencySymbol: values.currencySymbol?.trim(),
          enabled: !!values.currencyEnabled,
          displayCurrency: {enabled: !!values.displayEnabled},
          deposit: {
            enabled: !!values.depositEnabled,
            enabledPaymentMethods: values.depositMethods ?? [],
            limit: {
              daily: toNumber(values.depositDailyLimit),
              weekly: toNumber(values.depositWeeklyLimit),
              monthly: toNumber(values.depositMonthlyLimit),
            },
            // The stored array is replaced wholesale, so every method worth
            // keeping goes out on each save, not just the edited one. An empty
            // array is sent deliberately — it is how the last config is
            // deleted, and omitting the key instead would make the service
            // carry the old configs forward.
            paymentMethodConfigs: buildPaymentMethodConfigs(
              values.depositMethods,
              values.depositMethodConfigs,
            ),
          },
          withdrawal: {
            enabled: !!values.withdrawalEnabled,
            enabledPaymentMethods: values.withdrawalMethods ?? [],
            limit: {
              daily: toNumber(values.withdrawalDailyLimit),
              weekly: toNumber(values.withdrawalWeeklyLimit),
              monthly: toNumber(values.withdrawalMonthlyLimit),
            },
            paymentMethodConfigs: buildPaymentMethodConfigs(
              values.withdrawalMethods,
              values.withdrawalMethodConfigs,
            ),
          },
        },
      },
    })
      .then(({data: response}) => {
        const result = response?.updatePlatformWalletCurrencySettings;
        if (result?.__typename === 'Error') {
          // Surface the backend's own words rather than a generic failure, and
          // leave the confirm modal up so the operator can read the toast and
          // retry without losing what they were about to save.
          toast.error(result.message ?? 'The change was rejected');
          return;
        }
        toast.success(
          isUpdate
            ? `${values.currency} settings updated`
            : `${values.currency} created`,
        );
        setConfirming(false);
        navigate('/dashboard/wallet');
      })
      .catch(mutationError => {
        // The old screen did `.catch(error => toast.error(error))`, handing
        // toast an Error object, which renders as "[object Object]".
        toast.error(
          mutationError?.message ?? 'Something went wrong, please try again',
        );
      });
  };

  if (isUpdate && loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <Spin size="large" />
          <p>Loading {currency} settings…</p>
        </div>
      </div>
    );
  }

  const renderLimits = kind => (
    <div className={styles.limitsRow}>
      {limitFields(kind).map(field => (
        <Form.Item
          key={field.name}
          name={field.name}
          label={field.label}
          dependencies={field.deps}
          className={styles.limitField}
          rules={[
            {required: true, message: 'Required'},
            wholeNumberRule,
            field.rule,
          ]}>
          <InputNumber
            {...wholeAmountInputProps}
            className={styles.fullWidth}
            placeholder="0"
          />
        </Form.Item>
      ))}
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate('/dashboard/wallet')}>
          <ArrowLeftOutlined /> Wallet
        </button>
        <h1 className={styles.title}>
          {isUpdate ? `${currency} currency settings` : 'New wallet currency'}
        </h1>
        <p className={styles.subtitle}>
          {isUpdate
            ? 'Change how users deposit, withdraw and see this currency.'
            : 'Add a currency to the platform and set how users can move money in and out of it.'}
        </p>
      </div>

      {loadError && (
        <Alert
          className={styles.alert}
          type="error"
          showIcon
          message="Could not load this currency"
          description={loadError}
          action={
            <Button size="small" onClick={() => window.location.reload()}>
              Retry
            </Button>
          }
        />
      )}

      <Form
        form={form}
        onFinish={handleFinish}
        onValuesChange={handleValuesChange}
        layout="vertical"
        scrollToFirstError
        initialValues={DEFAULTS}
        className={styles.form}>
        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>Currency</h2>
            <p className={styles.cardHint}>
              How the currency is identified across the platform.
            </p>
          </div>

          <div className={styles.fieldsRow}>
            <Form.Item
              name="currency"
              label="Currency"
              className={styles.field}
              tooltip={
                isUpdate
                  ? 'The currency cannot be changed on an existing record.'
                  : undefined
              }
              rules={[{required: true, message: 'Pick a currency'}]}>
              {/* Locked when updating: changing it here would write the form
                  over a different currency's settings entirely. */}
              <Select
                showSearch
                optionFilterProp="label"
                disabled={isUpdate}
                placeholder="Select a currency"
                options={currencyOptions.filter(option =>
                  walletCurrencies.some(item => item.key === option.value),
                )}
              />
            </Form.Item>
            <Form.Item
              name="currencyName"
              label="Currency name"
              className={styles.field}
              rules={[
                {required: true, message: 'Enter a name'},
                {max: 60, message: 'Keep it under 60 characters'},
              ]}>
              <Input placeholder="US Dollars" />
            </Form.Item>
            <Form.Item
              name="currencySymbol"
              label="Symbol"
              className={styles.field}
              rules={[
                {required: true, message: 'Enter a symbol'},
                {max: 6, message: 'Symbols are at most 6 characters'},
              ]}>
              <Input placeholder="$" />
            </Form.Item>
          </div>

          <div className={styles.toggleRow}>
            <Form.Item
              name="currencyEnabled"
              valuePropName="checked"
              className={styles.toggleField}
              label="Currency enabled"
              extra="Turn off to stop all activity in this currency.">
              <Switch className={styles.switch} />
            </Form.Item>
            <Form.Item
              name="displayEnabled"
              valuePropName="checked"
              className={styles.toggleField}
              label="Shown to users"
              extra="Controls whether users see this currency in the app.">
              <Switch className={styles.switch} />
            </Form.Item>
          </div>
        </section>

        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>Deposits</h2>
            <p className={styles.cardHint}>
              How money comes in, and the most a user may deposit per period.
            </p>
          </div>

          <Form.Item
            name="depositEnabled"
            valuePropName="checked"
            className={styles.toggleField}
            label="Deposits enabled">
            <Switch className={styles.switch} />
          </Form.Item>

          <Form.Item
            name="depositMethods"
            label="Allowed deposit methods"
            className={styles.wideField}
            rules={[{required: true, message: 'Pick at least one method'}]}>
            {/* `mode="multiple"`, not "tags": tags let an operator invent a
                method the backend has no enum for, which fails on save. */}
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              placeholder="Select payment methods"
              options={methodOptions}
            />
          </Form.Item>

          {renderLimits('deposit')}

          {/* Rows are driven by the methods picked above, not by what is
              already stored: a method the currency accepts but has never been
              configured still needs a row, and one removed from the list
              should stop being editable. */}
          <Form.Item noStyle shouldUpdate>
            {({getFieldValue}) => (
              <Form.Item name="depositMethodConfigs" noStyle>
                <PaymentMethodConfigEditor
                  directionLabel="Deposit"
                  methods={getFieldValue('depositMethods')}
                  countries={countries}
                />
              </Form.Item>
            )}
          </Form.Item>
        </section>

        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>Withdrawals</h2>
            <p className={styles.cardHint}>
              How money goes out, and the most a user may withdraw per period.
            </p>
          </div>

          <Form.Item
            name="withdrawalEnabled"
            valuePropName="checked"
            className={styles.toggleField}
            label="Withdrawals enabled">
            <Switch className={styles.switch} />
          </Form.Item>

          <Form.Item
            name="withdrawalMethods"
            label="Allowed withdrawal methods"
            className={styles.wideField}
            rules={[{required: true, message: 'Pick at least one method'}]}>
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              placeholder="Select payment methods"
              options={methodOptions}
            />
          </Form.Item>

          {renderLimits('withdrawal')}

          {/* Rows are driven by the methods picked above, not by what is
              already stored: a method the currency accepts but has never been
              configured still needs a row, and one removed from the list
              should stop being editable. */}
          <Form.Item noStyle shouldUpdate>
            {({getFieldValue}) => (
              <Form.Item name="withdrawalMethodConfigs" noStyle>
                <PaymentMethodConfigEditor
                  directionLabel="Withdrawal"
                  methods={getFieldValue('withdrawalMethods')}
                  countries={countries}
                />
              </Form.Item>
            )}
          </Form.Item>
        </section>

        <div className={styles.footer}>
          <Button onClick={() => navigate('/dashboard/wallet')}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            className={styles.primaryButton}>
            {isUpdate ? 'Review changes' : 'Review and create'}
          </Button>
        </div>
      </Form>

      <CurrencyConfirmModal
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={handleConfirm}
        loading={saving}
        values={pendingValues}
        original={original}
        isUpdate={isUpdate}
      />
    </div>
  );
};

export default CreateWalletCurrency;
